import { useEffect, useState } from "react";
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import { useAuth } from "@/hooks/useAuth";
import DashboardPanel, { DashboardSpec } from "@/components/DashboardPanel";
import type { ConfiguredFilter, FilterSuggestionsResult } from "@/components/FilterSuggestionsDialog";
import { Loader2, LayoutDashboard, MessageSquare, Trash2, Bell, Plus, Bookmark, ChevronLeft, Copy, Pencil, Share2, MoreVertical, Search, LayoutTemplate } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { toTitleCase } from "@/lib/text-utils";
import DashboardSubscribeDialog from "@/components/admin/DashboardSubscribeDialog";
import CreateDashboardDialog from "@/components/admin/CreateDashboardDialog";
import AddWidgetDialog from "@/components/admin/AddWidgetDialog";
import EditWidgetDialog from "@/components/admin/EditWidgetDialog";
import ShareDialog from "@/components/admin/ShareDialog";
import { useAuditLog } from "@/hooks/useAuditLog";
import { useMcpSchema } from "@/hooks/useMcpSchema";
import { buildAutoFilters } from "@/lib/auto-filters";
import { TemplateBrowserDialog, TemplatePromptDialog } from "@/components/TemplateBrowserDialog";
import type { WorkflowTemplate } from "@/lib/template-constants";

interface DashboardEntry {
  id: string;
  title: string;
  spec: any;
  created_at: string;
  updated_at: string;
  conversation_id: string | null;
  conversation_title: string | null;
  bookmarked: boolean;
  /** True if this dashboard was shared with the user (not owned) */
  shared?: boolean;
  shared_via?: string;
  /** Cached widget data from last query execution for instant loading */
  cached_widget_data?: any;
}

const DashboardsContent = () => {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const { log: auditLog } = useAuditLog();
  const { schemaData } = useMcpSchema();
  const isViewer = profile?.viewer && !profile?.admin && !profile?.super_admin;
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState<DashboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedDashboard, setSelectedDashboard] = useState<DashboardSpec | null>(null);
  const [selectedHasCachedData, setSelectedHasCachedData] = useState(false);
  const [subscribeEntry, setSubscribeEntry] = useState<DashboardEntry | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renamingTitle, setRenamingTitle] = useState("");
  const [sharingEntry, setSharingEntry] = useState<DashboardEntry | null>(null);
  const [editWidgetIndex, setEditWidgetIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Template browser state
  const [dashboardTemplateBrowserOpen, setDashboardTemplateBrowserOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [templatePromptOpen, setTemplatePromptOpen] = useState(false);

  const fetchDashboards = async () => {
    setLoading(true);

    // Fetch own dashboards
    const { data, error } = await supabase
      .from("dashboard_snapshots")
      .select("id, title, spec, created_at, updated_at, conversation_id, conversations(title), cached_widget_data")
      .is("deleted_at", null)
      .order("updated_at", { ascending: false });

    if (error) {
      console.error("Dashboards fetch error:", error);
    }

    const ownEntries: DashboardEntry[] = !error && data
      ? data.map((d: any) => ({
          id: d.id,
          title: d.title,
          spec: d.spec,
          created_at: d.created_at,
          updated_at: d.updated_at,
          conversation_id: d.conversation_id ?? null,
          conversation_title: d.conversations?.title ?? null,
          bookmarked: !!d.bookmarked,
          cached_widget_data: d.cached_widget_data ?? null,
        }))
      : [];

    const ownIds = new Set(ownEntries.map((e) => e.id));

    // Fetch dashboards shared with the current user
    let sharedEntries: DashboardEntry[] = [];
    if (profile?.id && profile?.client_id) {
      // Get user's group IDs
      const { data: memberData } = await supabase
        .from("group_members" as any)
        .select("group_id")
        .eq("profile_id", profile.id);
      const userGroupIds = (memberData || []).map((m: any) => m.group_id);

      // Fetch all dashboard shares for this client
      const { data: sharesData } = await supabase
        .from("dashboard_shares" as any)
        .select("dashboard_id, share_type, group_id, profile_id")
        .eq("client_id", profile.client_id);

      if (sharesData) {
        // Filter shares relevant to this user
        const relevantDashboardIds = new Set<string>();
        const shareLabels: Record<string, string> = {};
        for (const share of sharesData as any[]) {
          const isRelevant =
            share.share_type === "public" ||
            (share.share_type === "employee" && share.profile_id === profile.id) ||
            (share.share_type === "group" && userGroupIds.includes(share.group_id));
          if (isRelevant && !ownIds.has(share.dashboard_id)) {
            relevantDashboardIds.add(share.dashboard_id);
            if (!shareLabels[share.dashboard_id]) {
              shareLabels[share.dashboard_id] = share.share_type === "public" ? "Public" : share.share_type === "group" ? "Group" : "Direct";
            }
          }
        }

        if (relevantDashboardIds.size > 0) {
          const { data: sharedDashboards } = await supabase
            .from("dashboard_snapshots")
            .select("id, title, spec, created_at, updated_at, conversation_id, conversations(title), cached_widget_data")
            .in("id", Array.from(relevantDashboardIds));

          if (sharedDashboards) {
            sharedEntries = sharedDashboards.map((d: any) => ({
              id: d.id,
              title: d.title,
              spec: d.spec,
              created_at: d.created_at,
              updated_at: d.updated_at,
              conversation_id: d.conversation_id ?? null,
              conversation_title: d.conversations?.title ?? null,
              bookmarked: false,
              shared: true,
              shared_via: shareLabels[d.id] || "Shared",
              cached_widget_data: d.cached_widget_data ?? null,
            }));
          }
        }
      }
    }

    setEntries([...ownEntries, ...sharedEntries]);
    setLoading(false);
  };

  useEffect(() => {
    if (!user) return;
    fetchDashboards();
  }, [user]);

  const handleSelect = (entry: DashboardEntry) => {
    setSelectedId(entry.id);
    // Use cached widget data for instant loading if available
    const cachedWidgets = entry.cached_widget_data;
    setSelectedHasCachedData(!!cachedWidgets);
    setSelectedDashboard({
      title: entry.title,
      widgets: cachedWidgets || entry.spec?.widgets || [],
      savedToolCalls: entry.spec?.savedToolCalls || [],
      dataSourceIds: entry.spec?.dataSourceIds,
      updatedAt: entry.updated_at,
      configuredFilters: entry.spec?.configuredFilters || [],
    });
    setSearchParams((prev) => {
      prev.set("dashboard", entry.id);
      return prev;
    });
    localStorage.setItem("last_dashboard_id", entry.id);
    auditLog({ action: "accessed", resourceType: "dashboard", resourceId: entry.id, resourceName: entry.title });
  };

  // Restore selection from URL after entries load
  useEffect(() => {
    if (entries.length === 0) return;
    const dashboardId = searchParams.get("dashboard") || localStorage.getItem("last_dashboard_id");
    if (dashboardId && dashboardId !== selectedId) {
      const entry = entries.find((e) => e.id === dashboardId);
      if (entry) {
        const cachedWidgets = entry.cached_widget_data;
        setSelectedId(entry.id);
        setSelectedHasCachedData(!!cachedWidgets);
        setSelectedDashboard({
          title: entry.title,
          widgets: cachedWidgets || entry.spec?.widgets || [],
          savedToolCalls: entry.spec?.savedToolCalls || [],
          dataSourceIds: entry.spec?.dataSourceIds,
          updatedAt: entry.updated_at,
          configuredFilters: entry.spec?.configuredFilters || [],
        });
        setSearchParams((prev) => {
          prev.set("dashboard", entry.id);
          return prev;
        });
      } else {
        localStorage.removeItem("last_dashboard_id");
      }
    }
  }, [entries]);

  // Detect return from query page with a widget draft (add widget)
  useEffect(() => {
    if (!selectedId) return;
    const widgetDraft = sessionStorage.getItem("widget_draft");
    const widgetQuery = sessionStorage.getItem("widget_selected_query");
    if (widgetDraft && widgetQuery) {
      setAddWidgetOpen(true);
    }
  }, [selectedId]);

  // Detect return from query page with an edit widget draft
  useEffect(() => {
    if (!selectedId) return;
    const editQuery = sessionStorage.getItem("edit_widget_selected_query");
    const editDraftRaw = sessionStorage.getItem("edit_widget_draft_index");
    if (editQuery && editDraftRaw !== null) {
      try {
        const idx = parseInt(editDraftRaw, 10);
        if (!isNaN(idx)) setEditWidgetIndex(idx);
      } catch { /* ignore */ }
      sessionStorage.removeItem("edit_widget_draft_index");
    }
  }, [selectedId]);

  const handleDelete = async (id: string) => {
    // Soft-delete: mark as deleted instead of removing
    await supabase.from("dashboard_snapshots").update({ deleted_at: new Date().toISOString(), deleted_by: user?.id } as any).eq("id", id);
    setEntries((prev) => prev.filter((d) => d.id !== id));
    if (selectedId === id) {
      setSelectedId(null);
      setSelectedDashboard(null);
      setSearchParams((prev) => {
        prev.delete("dashboard");
        return prev;
      });
    }
    auditLog({ action: "deleted", resourceType: "dashboard", resourceId: id });
    toast({ title: "Deleted", description: "Dashboard moved to recycle bin" });
  };

  const toggleBookmark = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const entry = entries.find((d) => d.id === id);
    if (!entry) return;
    const newVal = !entry.bookmarked;
    setEntries((prev) => prev.map((d) => d.id === id ? { ...d, bookmarked: newVal } : d));
    await supabase.from("dashboard_snapshots").update({ bookmarked: newVal } as any).eq("id", id);
  };

  const startRenaming = (entry: DashboardEntry, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenamingId(entry.id);
    setRenamingTitle(entry.title);
  };

  const handleRename = async (id: string, newTitle: string) => {
    const trimmed = newTitle.trim();
    if (!trimmed) {
      setRenamingId(null);
      return;
    }
    const { error } = await supabase
      .from("dashboard_snapshots")
      .update({ title: trimmed, updated_at: new Date().toISOString() } as any)
      .eq("id", id);

    if (error) {
      toast({ title: "Error", description: "Failed to rename dashboard", variant: "destructive" });
    } else {
      setEntries((prev) => prev.map((d) => d.id === id ? { ...d, title: trimmed } : d));
      if (selectedId === id && selectedDashboard) {
        setSelectedDashboard({ ...selectedDashboard, title: trimmed });
      }
      auditLog({ action: "edited", resourceType: "dashboard", resourceId: id, resourceName: trimmed });
      toast({ title: "Renamed", description: "Dashboard name updated" });
    }
    setRenamingId(null);
  };

  const handleClone = async (entry: DashboardEntry, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const clonedSpec = entry.spec ? JSON.parse(JSON.stringify(entry.spec)) : { widgets: [] };
      const { data, error } = await supabase
        .from("dashboard_snapshots")
        .insert({
          title: `${entry.title} (Copy)`,
          spec: clonedSpec as any,
          client_id: profile?.client_id ?? "",
          profile_id: profile?.id,
        } as any)
        .select("id, title, spec, created_at, updated_at, conversation_id")
        .single();

      if (error) throw error;

      const newEntry: DashboardEntry = {
        id: data.id,
        title: data.title,
        spec: data.spec,
        created_at: data.created_at,
        updated_at: data.updated_at,
        conversation_id: data.conversation_id ?? null,
        conversation_title: null,
        bookmarked: false,
      };
      setEntries((prev) => [newEntry, ...prev]);
      auditLog({ action: "created", resourceType: "dashboard", resourceId: data.id, resourceName: data.title });
      toast({ title: "Cloned", description: "Dashboard duplicated successfully" });
    } catch (err: any) {
      console.error("Clone dashboard error:", err);
      toast({ title: "Error", description: err.message || "Failed to clone dashboard", variant: "destructive" });
    }
  };

  const handleCreate = async (name: string, description: string, prompt?: string) => {
    setCreating(true);
    try {
      let widgets: any[] = [];

      // If a prompt is provided, generate widgets via AI
      if (prompt) {
        // 1. Fetch database schema for context
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const headers = await buildEdgeFunctionHeaders();

        let schemaContext = "";
        try {
          const schemaRes = await fetch(`${supabaseUrl}/functions/v1/mcp-api/getSchema`, {
            method: "GET",
            headers,
          });
          if (schemaRes.ok) {
            const schemaData = await schemaRes.json();
            const tables = Array.isArray(schemaData) ? schemaData : schemaData?.tables || [];
            schemaContext = (Array.isArray(tables) ? tables : [])
              .map((t: any) => {
                const cols = (t.columns || []).map((c: any) => `  ${c.column_name || c.name} (${c.data_type || c.type})`).join("\n");
                return `Table: ${t.table_name || t.name}\n${cols}`;
              })
              .join("\n\n");
          }
        } catch { /* schema fetch is best-effort */ }

        // 2. Call generate-dashboard edge function with prompt
        const genRes = await fetch(`${supabaseUrl}/functions/v1/generate-dashboard`, {
          method: "POST",
          headers,
          body: JSON.stringify({ prompt, schemaContext }),
        });

        if (!genRes.ok) {
          const errData = await genRes.json().catch(() => ({ error: "Failed to generate dashboard" }));
          throw new Error(errData.error || "Failed to generate dashboard");
        }

        const raw = await genRes.json();
        const { _usage, ...generatedSpec } = raw;
        widgets = generatedSpec.widgets || [];

        // Raw SQL is stored directly in each widget's sqlQuery field.
        // Set _queryName from the title for display purposes.
        widgets.forEach((w: any) => {
          if (w.sqlQuery?.trim()) {
            w._queryName = w.title || "Dashboard Query";
          }
        });
      }

      // Auto-detect filters from SQL WHERE clauses in widget queries
      const autoFilters = buildAutoFilters(widgets, schemaData);
      if (autoFilters.length > 0) {
        console.log(`Auto-detected ${autoFilters.length} filter(s) from widget queries`);
      }

      const spec = {
        widgets,
        description,
        ...(autoFilters.length > 0 ? { configuredFilters: autoFilters } : {}),
      };
      const { data, error } = await supabase
        .from("dashboard_snapshots")
        .insert({
          title: name,
          spec: spec as any,
          client_id: profile?.client_id ?? "",
          profile_id: profile?.id,
        } as any)
        .select("id, title, spec, created_at, updated_at, conversation_id")
        .single();

      if (error) throw error;

      const newEntry: DashboardEntry = {
        id: data.id,
        title: data.title,
        spec: data.spec,
        created_at: data.created_at,
        updated_at: data.updated_at,
        conversation_id: data.conversation_id ?? null,
        conversation_title: null,
        bookmarked: false,
      };
      setEntries((prev) => [newEntry, ...prev]);
      handleSelect(newEntry);
      setCreateOpen(false);
      auditLog({ action: "created", resourceType: "dashboard", resourceId: data.id, resourceName: name });
      toast({
        title: "Created",
        description: prompt ? "Dashboard generated with widgets!" : "Dashboard created. Add widgets now!",
      });
    } catch (err: any) {
      console.error("Create dashboard error:", err);
      toast({ title: "Error", description: err.message || "Failed to create dashboard", variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const handleAddWidget = async (widget: {
    type: string;
    title: string;
    sqlQuery?: string;
    _queryName?: string;
    queryId?: string;
    xKey?: string;
    yKeys?: string[];
    width?: number;
    height?: number;
  }) => {
    if (!selectedId) return;

    const entry = entries.find((e) => e.id === selectedId);
    if (!entry) return;

    const existingWidgets = entry.spec?.widgets || [];

    let newWidget: any;
    const baseFields: Record<string, any> = {};
    if (widget.sqlQuery) baseFields.sqlQuery = widget.sqlQuery;
    if (widget._queryName) baseFields._queryName = widget._queryName;
    if (widget.width) baseFields.width = widget.width;
    if (widget.height) baseFields.height = widget.height;
    if (widget.type === "stat") {
      newWidget = {
        type: "stat",
        title: widget.title,
        value: "–",
        ...baseFields,
      };
    } else if (widget.type === "table") {
      newWidget = {
        type: "table",
        title: widget.title,
        columns: [],
        rows: [],
        ...baseFields,
      };
    } else if (widget.type === "pie") {
      newWidget = {
        type: "pie",
        title: widget.title,
        data: [],
        ...baseFields,
      };
    } else {
      newWidget = {
        type: widget.type,
        title: widget.title,
        data: [],
        xKey: widget.xKey || "x",
        yKeys: widget.yKeys || ["value"],
        ...baseFields,
      };
    }

    const updatedWidgets = [...existingWidgets, newWidget];
    const updatedSpec = { ...entry.spec, widgets: updatedWidgets };

    const { error } = await supabase
      .from("dashboard_snapshots")
      .update({ spec: updatedSpec as any, updated_at: new Date().toISOString(), cached_widget_data: null } as any)
      .eq("id", selectedId);

    if (error) {
      toast({ title: "Error", description: "Failed to add widget", variant: "destructive" });
      return;
    }

    setEntries((prev) =>
      prev.map((e) => (e.id === selectedId ? { ...e, spec: updatedSpec, cached_widget_data: null } : e))
    );
    setSelectedHasCachedData(false);
    setSelectedDashboard({
      title: entry.title,
      widgets: updatedWidgets,
      savedToolCalls: entry.spec?.savedToolCalls || [],
      dataSourceIds: entry.spec?.dataSourceIds,
      updatedAt: new Date().toISOString(),
    });

    toast({ title: "Widget added", description: `${widget.title} added to dashboard` });
  };

  const handleDeleteWidget = async (widgetIndex: number) => {
    if (!selectedId) return;

    const entry = entries.find((e) => e.id === selectedId);
    if (!entry) return;

    const existingWidgets = entry.spec?.widgets || [];
    const updatedWidgets = existingWidgets.filter((_: any, i: number) => i !== widgetIndex);
    const updatedSpec = { ...entry.spec, widgets: updatedWidgets };

    const { error } = await supabase
      .from("dashboard_snapshots")
      .update({ spec: updatedSpec as any, updated_at: new Date().toISOString(), cached_widget_data: null } as any)
      .eq("id", selectedId);

    if (error) {
      toast({ title: "Error", description: "Failed to delete widget", variant: "destructive" });
      return;
    }

    setEntries((prev) =>
      prev.map((e) => (e.id === selectedId ? { ...e, spec: updatedSpec, cached_widget_data: null } : e))
    );
    setSelectedHasCachedData(false);
    setSelectedDashboard({
      title: entry.title,
      widgets: updatedWidgets,
      savedToolCalls: entry.spec?.savedToolCalls || [],
      dataSourceIds: entry.spec?.dataSourceIds,
      updatedAt: new Date().toISOString(),
      configuredFilters: entry.spec?.configuredFilters || [],
    });

    toast({ title: "Widget removed", description: "Widget deleted from dashboard" });
  };

  const handleCloneWidget = async (widgetIndex: number) => {
    if (!selectedId) return;

    const entry = entries.find((e) => e.id === selectedId);
    if (!entry) return;

    const existingWidgets = entry.spec?.widgets || [];
    if (widgetIndex < 0 || widgetIndex >= existingWidgets.length) return;

    const original = existingWidgets[widgetIndex];
    const cloned = { ...JSON.parse(JSON.stringify(original)), title: `${original.title || "Widget"} (Copy)` };
    const updatedWidgets = [...existingWidgets, cloned];
    const updatedSpec = { ...entry.spec, widgets: updatedWidgets };

    const { error } = await supabase
      .from("dashboard_snapshots")
      .update({ spec: updatedSpec as any, updated_at: new Date().toISOString(), cached_widget_data: null } as any)
      .eq("id", selectedId);

    if (error) {
      toast({ title: "Error", description: "Failed to clone widget", variant: "destructive" });
      return;
    }

    setEntries((prev) =>
      prev.map((e) => (e.id === selectedId ? { ...e, spec: updatedSpec, cached_widget_data: null } : e))
    );
    setSelectedHasCachedData(false);
    setSelectedDashboard({
      title: entry.title,
      widgets: updatedWidgets,
      savedToolCalls: entry.spec?.savedToolCalls || [],
      dataSourceIds: entry.spec?.dataSourceIds,
      updatedAt: new Date().toISOString(),
      configuredFilters: entry.spec?.configuredFilters || [],
    });

    toast({ title: "Widget cloned", description: `"${original.title || "Widget"}" duplicated` });
  };

  const handleWidgetQueryUpdate = async (widgetIndex: number, sqlQuery: string, queryName?: string) => {
    if (!selectedId) return;

    const entry = entries.find((e) => e.id === selectedId);
    if (!entry) return;

    const existingWidgets = entry.spec?.widgets || [];
    if (widgetIndex < 0 || widgetIndex >= existingWidgets.length) return;

    const updatedWidgets = existingWidgets.map((w: any, i: number) =>
      i === widgetIndex ? { ...w, sqlQuery, ...(queryName ? { _queryName: queryName } : {}) } : w
    );
    const updatedSpec = { ...entry.spec, widgets: updatedWidgets };

    const { error } = await supabase
      .from("dashboard_snapshots")
      .update({ spec: updatedSpec as any, updated_at: new Date().toISOString(), cached_widget_data: null } as any)
      .eq("id", selectedId);

    if (error) {
      toast({ title: "Error", description: "Failed to save widget query", variant: "destructive" });
      return;
    }

    setEntries((prev) =>
      prev.map((e) => (e.id === selectedId ? { ...e, spec: updatedSpec, cached_widget_data: null } : e))
    );
    setSelectedHasCachedData(false);
  };

  const handleUpdateWidget = async (widgetIndex: number, updates: Record<string, any>) => {
    if (!selectedId) return;

    const entry = entries.find((e) => e.id === selectedId);
    if (!entry) return;

    const existingWidgets = entry.spec?.widgets || [];
    if (widgetIndex < 0 || widgetIndex >= existingWidgets.length) return;

    const oldWidget = existingWidgets[widgetIndex];
    const updatedWidget = { ...oldWidget, ...updates };

    // If the type changed, reset type-specific data fields to safe defaults
    if (updates.type && updates.type !== oldWidget.type) {
      if (updates.type === "stat") {
        updatedWidget.value = updatedWidget.value || "–";
        delete updatedWidget.data;
        delete updatedWidget.columns;
        delete updatedWidget.rows;
      } else if (updates.type === "table") {
        updatedWidget.columns = updatedWidget.columns || [];
        updatedWidget.rows = updatedWidget.rows || [];
        delete updatedWidget.data;
        delete updatedWidget.value;
      } else if (updates.type === "pie") {
        updatedWidget.data = updatedWidget.data || [];
        delete updatedWidget.columns;
        delete updatedWidget.rows;
        delete updatedWidget.value;
      } else {
        // bar, line, area
        updatedWidget.data = updatedWidget.data || [];
        updatedWidget.xKey = updatedWidget.xKey || "x";
        updatedWidget.yKeys = updatedWidget.yKeys || ["value"];
        delete updatedWidget.columns;
        delete updatedWidget.rows;
        delete updatedWidget.value;
      }
    }

    const updatedWidgets = existingWidgets.map((w: any, i: number) =>
      i === widgetIndex ? updatedWidget : w
    );
    const updatedSpec = { ...entry.spec, widgets: updatedWidgets };

    const { error } = await supabase
      .from("dashboard_snapshots")
      .update({ spec: updatedSpec as any, updated_at: new Date().toISOString(), cached_widget_data: null } as any)
      .eq("id", selectedId);

    if (error) {
      toast({ title: "Error", description: "Failed to update widget", variant: "destructive" });
      return;
    }

    setEntries((prev) =>
      prev.map((e) => (e.id === selectedId ? { ...e, spec: updatedSpec, cached_widget_data: null } : e))
    );
    setSelectedHasCachedData(false);
    setSelectedDashboard({
      title: entry.title,
      widgets: updatedWidgets,
      savedToolCalls: entry.spec?.savedToolCalls || [],
      dataSourceIds: entry.spec?.dataSourceIds,
      updatedAt: new Date().toISOString(),
      configuredFilters: entry.spec?.configuredFilters || [],
    });

    toast({ title: "Widget updated", description: "Widget changes saved" });
  };

  const handleSaveFilters = async (result: FilterSuggestionsResult) => {
    if (!selectedId) return;

    const entry = entries.find((e) => e.id === selectedId);
    if (!entry) return;

    const { filters, updatedWidgets } = result;

    // Merge updated SQL queries into existing widgets
    const currentWidgets = entry.spec?.widgets || [];
    const mergedWidgets = currentWidgets.map((w: any, i: number) => {
      const updated = updatedWidgets[i];
      if (updated && updated.sqlQuery !== w.sqlQuery) {
        return { ...w, sqlQuery: updated.sqlQuery };
      }
      return w;
    });

    const updatedSpec = {
      ...entry.spec,
      widgets: mergedWidgets,
      configuredFilters: filters,
    };

    const { error } = await supabase
      .from("dashboard_snapshots")
      .update({ spec: updatedSpec as any, updated_at: new Date().toISOString(), cached_widget_data: null } as any)
      .eq("id", selectedId);

    if (error) {
      toast({ title: "Error", description: "Failed to save filters", variant: "destructive" });
      return;
    }

    setEntries((prev) =>
      prev.map((e) => (e.id === selectedId ? { ...e, spec: updatedSpec, cached_widget_data: null } : e))
    );
    setSelectedHasCachedData(false);
    setSelectedDashboard({
      title: entry.title,
      widgets: mergedWidgets,
      savedToolCalls: entry.spec?.savedToolCalls || [],
      dataSourceIds: entry.spec?.dataSourceIds,
      updatedAt: new Date().toISOString(),
      configuredFilters: filters,
    });

    toast({
      title: "Filters saved",
      description: `${filters.length} filter${filters.length !== 1 ? "s" : ""} configured. Queries updated with filter placeholders.`,
    });
  };

  const handleDownloadPdf = async () => {
    try {
      const dashboardEl = document.querySelector("[data-dashboard-panel]") as HTMLElement | null;
      if (!dashboardEl) {
        toast({ title: "Error", description: "Dashboard panel not found.", variant: "destructive" });
        return;
      }

      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      // Temporarily expand scrollable containers so html2canvas captures ALL content
      const scrollContainer = dashboardEl.querySelector(".overflow-y-auto") as HTMLElement | null;
      const savedStyles: { el: HTMLElement; overflow: string; height: string; maxHeight: string; flex: string }[] = [];

      savedStyles.push({
        el: dashboardEl,
        overflow: dashboardEl.style.overflow,
        height: dashboardEl.style.height,
        maxHeight: dashboardEl.style.maxHeight,
        flex: dashboardEl.style.flex,
      });
      dashboardEl.style.overflow = "visible";
      dashboardEl.style.height = "auto";
      dashboardEl.style.maxHeight = "none";
      dashboardEl.style.flex = "none";

      if (scrollContainer) {
        savedStyles.push({
          el: scrollContainer,
          overflow: scrollContainer.style.overflow,
          height: scrollContainer.style.height,
          maxHeight: scrollContainer.style.maxHeight,
          flex: scrollContainer.style.flex,
        });
        scrollContainer.style.overflow = "visible";
        scrollContainer.style.height = "auto";
        scrollContainer.style.maxHeight = "none";
        scrollContainer.style.flex = "none";
      }

      // Allow the layout to reflow
      await new Promise((r) => setTimeout(r, 300));

      const canvas = await html2canvas(dashboardEl, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
        windowHeight: dashboardEl.scrollHeight,
      });

      // Restore original styles
      for (const s of savedStyles) {
        s.el.style.overflow = s.overflow;
        s.el.style.height = s.height;
        s.el.style.maxHeight = s.maxHeight;
        s.el.style.flex = s.flex;
      }

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;

      const a4Width = 841.89;
      const a4Height = 595.28;
      const margin = 20;
      const usableWidth = a4Width - margin * 2;
      const usableHeight = a4Height - margin * 2;

      const scaleFactor = usableWidth / canvasWidth;
      const scaledHeight = canvasHeight * scaleFactor;
      const totalPages = Math.ceil(scaledHeight / usableHeight);

      const pdf = new jsPDF("l", "pt", "a4");

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage("a4", "l");

        const srcY = Math.round((page * usableHeight) / scaleFactor);
        const srcH = Math.min(
          Math.round(usableHeight / scaleFactor),
          canvasHeight - srcY
        );

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvasWidth;
        pageCanvas.height = srcH;
        const ctx = pageCanvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          ctx.drawImage(canvas, 0, srcY, canvasWidth, srcH, 0, 0, canvasWidth, srcH);
        }

        const pageImgData = pageCanvas.toDataURL("image/png");
        const drawHeight = srcH * scaleFactor;
        pdf.addImage(pageImgData, "PNG", margin, margin, usableWidth, drawHeight);
      }

      const pdfTitle = selectedDashboard?.title || "Dashboard";
      pdf.save(`${pdfTitle.replace(/[^a-zA-Z0-9_-]/g, "_")}.pdf`);

      toast({ title: "Downloaded", description: "Dashboard PDF saved" });
    } catch (err: any) {
      console.error("PDF download error:", err);
      toast({ title: "Error", description: "Failed to download PDF", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (entries.length === 0 && !createOpen) {
    return (
      <>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent">
            <LayoutDashboard className="h-8 w-8 text-accent-foreground" />
          </div>
          <h1 className="text-2xl font-semibold">No Dashboards Yet</h1>
          <p className="max-w-md text-center text-muted-foreground">
            {isViewer
              ? "No dashboards are available to view at this time."
              : "Generate dashboards from your chat conversations or create one manually."}
          </p>
          {!isViewer && (
            <div className="flex flex-wrap gap-2 justify-center">
              <Button onClick={() => setCreateOpen(true)}>
                <Plus className="mr-1 h-4 w-4" /> Create Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => setDashboardTemplateBrowserOpen(true)}
                className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              >
                <LayoutTemplate className="h-4 w-4" />
                Dashboard Templates
              </Button>
            </div>
          )}
        </div>
        {!isViewer && (
          <CreateDashboardDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={handleCreate} saving={creating} />
        )}
      </>
    );
  }

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      {/* Sidebar list - hidden on mobile when a dashboard is selected */}
      <div className={cn(
        "border-r border-border flex flex-col overflow-hidden",
        "w-full md:w-72 md:shrink-0",
        selectedId ? "hidden md:flex" : "flex"
      )}>
        <div className="flex items-center gap-2 border-b border-border px-4 py-3">
          <LayoutDashboard className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold">All Dashboards</span>
          <span className="ml-auto text-xs text-muted-foreground">{entries.length}</span>
          {!isViewer && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 ml-1"
              onClick={() => setCreateOpen(true)}
              title="Create Dashboard"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          )}
          {!isViewer && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setDashboardTemplateBrowserOpen(true)}
              title="Dashboard Templates"
            >
              <LayoutTemplate className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <div className="px-3 py-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search dashboards…"
              className="h-8 pl-8 text-sm"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {entries.filter((e) => e.title.toLowerCase().includes(searchQuery.toLowerCase())).map((entry) => (
            <button
              key={entry.id}
              onClick={() => handleSelect(entry)}
              className={`group w-full flex flex-col items-start gap-1 border-b border-border px-4 py-3 text-left transition-colors hover:bg-accent ${
                selectedId === entry.id ? "bg-accent" : ""
              }`}
            >
              <div className="flex w-full items-start justify-between gap-2">
                {renamingId === entry.id ? (
                  <input
                    autoFocus
                    className="flex-1 text-sm font-medium bg-background border border-input rounded px-1.5 py-0.5"
                    value={renamingTitle}
                    onChange={(e) => setRenamingTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRename(entry.id, renamingTitle);
                      if (e.key === "Escape") setRenamingId(null);
                    }}
                    onBlur={() => handleRename(entry.id, renamingTitle)}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium leading-tight line-clamp-2">{toTitleCase(entry.title)}</span>
                    {entry.shared && (
                      <Badge variant="outline" className="text-[10px] px-1 py-0 mt-0.5">
                        {entry.shared_via || "Shared"}
                      </Badge>
                    )}
                  </div>
                )}
                <div className="flex shrink-0 gap-0.5">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn("h-6 w-6", entry.bookmarked ? "text-primary" : "text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100")}
                    onClick={(e) => toggleBookmark(entry.id, e)}
                    title={entry.bookmarked ? "Remove bookmark" : "Bookmark"}
                  >
                    <Bookmark className={cn("h-3.5 w-3.5", entry.bookmarked && "fill-primary")} />
                  </Button>
                  {!isViewer && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); startRenaming(entry, e as any); }}>
                          <Pencil className="mr-2 h-3.5 w-3.5" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSharingEntry(entry); }}>
                          <Share2 className="mr-2 h-3.5 w-3.5" />
                          Share
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleClone(entry, e as any); }}>
                          <Copy className="mr-2 h-3.5 w-3.5" />
                          Clone
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSubscribeEntry(entry); }}>
                          <Bell className="mr-2 h-3.5 w-3.5" />
                          Subscribe
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                        >
                          <Trash2 className="mr-2 h-3.5 w-3.5" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              {entry.conversation_title && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MessageSquare className="h-3 w-3 shrink-0" />
                  <span className="truncate max-w-[180px]">{entry.conversation_title}</span>
                </div>
              )}
              <span className="text-[10px] text-muted-foreground">
                Updated: {format(new Date(entry.updated_at), "MMM d, yyyy · h:mm a")}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Detail view - full width on mobile when dashboard is selected */}
      <div className={cn(
        "flex-1 min-w-0 flex flex-col",
        selectedId ? "flex" : "hidden md:flex"
      )}>
        {selectedDashboard ? (
          <>
            {/* Mobile back button */}
            <div className="flex items-center border-b border-border px-4 py-2 gap-2 md:hidden">
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 shrink-0"
                onClick={() => {
                  setSelectedId(null);
                  setSelectedDashboard(null);
                  setSelectedHasCachedData(false);
                  setSearchParams((prev) => { prev.delete("dashboard"); return prev; });
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex-1 min-h-0">
              <DashboardPanel
                dashboard={selectedDashboard}
                loading={false}
                onClose={() => {
                  setSelectedId(null);
                  setSelectedDashboard(null);
                  setSelectedHasCachedData(false);
                  setSearchParams((prev) => { prev.delete("dashboard"); return prev; });
                }}
                onDelete={isViewer ? undefined : () => selectedId && handleDelete(selectedId)}
                snapshotId={selectedId}
                onSaveFilters={isViewer ? undefined : handleSaveFilters}
                onRename={isViewer ? undefined : (newTitle) => selectedId && handleRename(selectedId, newTitle)}
                onWidgetEdit={isViewer ? undefined : (index) => setEditWidgetIndex(index)}
                onWidgetDelete={isViewer ? undefined : handleDeleteWidget}
                onWidgetClone={isViewer ? undefined : handleCloneWidget}
                onWidgetQueryUpdate={isViewer ? undefined : handleWidgetQueryUpdate}
                onAddWidget={isViewer ? undefined : () => setAddWidgetOpen(true)}
                onDownloadPdf={handleDownloadPdf}
                hasCachedData={selectedHasCachedData}
                onCacheUpdate={(widgets) => {
                  if (!selectedId) return;
                  // Save cached widget data to DB for instant loading next time
                  supabase
                    .from("dashboard_snapshots")
                    .update({ cached_widget_data: widgets } as any)
                    .eq("id", selectedId)
                    .then();
                  // Update local entry so re-selecting uses cache
                  setEntries((prev) =>
                    prev.map((e) => e.id === selectedId ? { ...e, cached_widget_data: widgets } : e)
                  );
                }}
              />
            </div>
          </>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-muted-foreground">
            <LayoutDashboard className="h-10 w-10" />
            <p className="text-sm">Select a dashboard to view</p>
          </div>
        )}
      </div>

      {!isViewer && (
        <>
          <CreateDashboardDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={handleCreate} saving={creating} />
          <AddWidgetDialog
            open={addWidgetOpen}
            onOpenChange={setAddWidgetOpen}
            dashboardId={selectedId}
            onAdd={handleAddWidget}
          />
          {editWidgetIndex !== null && selectedId && (() => {
            const entry = entries.find((e) => e.id === selectedId);
            const widget = entry?.spec?.widgets?.[editWidgetIndex] ?? null;
            return (
              <EditWidgetDialog
                open={true}
                onOpenChange={(open) => { if (!open) setEditWidgetIndex(null); }}
                widget={widget}
                widgetIndex={editWidgetIndex}
                dashboardId={selectedId}
                onSave={(updates) => handleUpdateWidget(editWidgetIndex, updates)}
                onDelete={() => { handleDeleteWidget(editWidgetIndex); setEditWidgetIndex(null); }}
              />
            );
          })()}
        </>
      )}

      {subscribeEntry && !isViewer && (
        <DashboardSubscribeDialog
          open={!!subscribeEntry}
          onOpenChange={(open) => !open && setSubscribeEntry(null)}
          dashboardSnapshotId={subscribeEntry.id}
          dashboardTitle={subscribeEntry.title}
        />
      )}

      {sharingEntry && (
        <ShareDialog
          open={!!sharingEntry}
          onOpenChange={(open) => !open && setSharingEntry(null)}
          resourceType="dashboard"
          resourceId={sharingEntry.id}
          resourceTitle={sharingEntry.title}
        />
      )}

      {/* Dashboard Templates */}
      <TemplateBrowserDialog
        open={dashboardTemplateBrowserOpen}
        onClose={() => setDashboardTemplateBrowserOpen(false)}
        templateType="dashboard"
        title="Choose a Dashboard Template"
        description="Browse pre-built dashboard templates and generate them with AI"
        onSelectTemplate={(t) => {
          setDashboardTemplateBrowserOpen(false);
          setSelectedTemplate(t);
          setTemplatePromptOpen(true);
        }}
      />

      <TemplatePromptDialog
        open={templatePromptOpen}
        onClose={() => { setTemplatePromptOpen(false); setSelectedTemplate(null); }}
        template={selectedTemplate}
        generateLabel="Generate Dashboard"
        placeholder="Describe the dashboard you want to build..."
        onGenerate={async (prompt) => {
          setTemplatePromptOpen(false);
          const name = selectedTemplate?.name ?? "New Dashboard";
          setSelectedTemplate(null);
          await handleCreate(name, "", prompt);
        }}
      />
    </div>
  );
};

const Dashboards = () => {
  return (
    <div className="flex flex-1 flex-col min-h-0">
      <DashboardsContent />
    </div>
  );
};

export default Dashboards;
